// Trang quản trị Kênh cộng đồng: danh sách bài đăng deal + ghim + đăng/sửa/xoá
// + quản lý danh mục + duyệt bài user gửi (tab "Chờ duyệt").
// Data-fetching/moderation logic ở use-community-post-moderation.js (gọn file này).
import React, { useCallback, useEffect, useState } from "react";
import { Table, Button, Space, Segmented, Badge, message } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faListUl, faGaugeHigh, faKey, faPaperPlane, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import communityApi from "../../../api/communityApi";
import { useAppSelector } from "../../../store";
import CommunityPostForm from "./community-post-form";
import CommunityCategoriesModal from "./community-categories-modal";
import CommunityPostLimitModal from "./community-post-limit-modal";
import CommunityHideDaysModal from "./community-hide-days-modal";
import CommunityPostFilterBar from "./community-post-filter-bar";
import CommunityApiKeysModal from "./community-api-keys-modal";
import CommunityTelegramModal from "./community-telegram-modal";
import CommunityRejectReasonModal from "./community-reject-reason-modal";
import { buildCommunityPostColumns } from "./community-post-columns";
import { buildCommunityPendingColumns } from "./community-pending-columns";
import { useCommunityPostModeration } from "./use-community-post-moderation";

function CommunityPostList() {
    const {
        view,
        setView,
        posts,
        loading,
        pagination,
        pendingPosts,
        pendingPagination,
        pendingTotal,
        rejectPostId,
        setRejectPostId,
        filters,
        setFilters,
        hideDays,
        fetchPosts,
        handleTableChange,
        handleApprove,
        handleRejectSubmit,
        handleDelete,
        handleTogglePinned,
    } = useCommunityPostModeration();

    const [categories, setCategories] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [categoriesOpen, setCategoriesOpen] = useState(false);
    const [postLimitOpen, setPostLimitOpen] = useState(false);
    const [hideDaysOpen, setHideDaysOpen] = useState(false);
    const [apiKeysOpen, setApiKeysOpen] = useState(false);
    const [telegramOpen, setTelegramOpen] = useState(false);
    const [editingPost, setEditingPost] = useState(null);

    // Cấp/thu hồi API key cho bot là việc của admin — mod có quyền 'community'
    // vẫn quản lý bài bình thường nhưng không tự phát key được (API cũng chặn).
    const isAdmin = useAppSelector((state) => state.auth.userInfo)?.role === "admin";

    const fetchCategories = useCallback(async () => {
        try {
            const { data } = await communityApi.getCategories();
            setCategories(data.categories || []);
        } catch {
            message.error("Không thể tải danh mục");
        }
    }, []);

    useEffect(() => {
        fetchPosts(1, 20);
        fetchCategories();
    }, [fetchPosts, fetchCategories]);

    const openCreateForm = () => {
        setEditingPost(null);
        setFormOpen(true);
    };

    const openEditForm = (record) => {
        setEditingPost(record);
        setFormOpen(true);
    };

    const columns = buildCommunityPostColumns({
        onTogglePinned: handleTogglePinned,
        onEdit: openEditForm,
        onDelete: handleDelete,
        hideDays,
    });
    const pendingColumns = buildCommunityPendingColumns({
        onApprove: handleApprove,
        onReject: setRejectPostId,
    });

    return (
        <div className="flex-1 p-6 overflow-auto">
            <div className="flex flex-wrap gap-3 justify-between items-center mb-5">
                <h1 className="text-2xl font-bold">Kênh Cộng Đồng</h1>
                <Space>
                    {isAdmin && (
                        <Button icon={<FontAwesomeIcon icon={faPaperPlane} />} onClick={() => setTelegramOpen(true)}>
                            Bot Telegram
                        </Button>
                    )}
                    {isAdmin && (
                        <Button icon={<FontAwesomeIcon icon={faKey} />} onClick={() => setApiKeysOpen(true)}>
                            API key
                        </Button>
                    )}
                    <Button icon={<FontAwesomeIcon icon={faEyeSlash} />} onClick={() => setHideDaysOpen(true)}>
                        {hideDays > 0 ? `Tự ẩn sau ${hideDays} ngày` : "Tự ẩn bài"}
                    </Button>
                    <Button icon={<FontAwesomeIcon icon={faGaugeHigh} />} onClick={() => setPostLimitOpen(true)}>
                        Giới hạn đăng bài
                    </Button>
                    <Button icon={<FontAwesomeIcon icon={faListUl} />} onClick={() => setCategoriesOpen(true)}>
                        Danh mục
                    </Button>
                    <Button
                        type="primary"
                        icon={<FontAwesomeIcon icon={faPlus} className="mr-1" />}
                        onClick={openCreateForm}
                    >
                        Đăng deal mới
                    </Button>
                </Space>
            </div>

            <Segmented
                className="mb-4"
                value={view}
                onChange={setView}
                options={[
                    { label: "Bài đăng", value: "active" },
                    {
                        // Badge nằm TRONG luồng (không dùng offset): Segmented cắt phần
                        // tràn ra ngoài ô nên số đếm kiểu đè góc bị che mất.
                        label: (
                            <span className="inline-flex items-center gap-2">
                                Chờ duyệt
                                {pendingTotal > 0 && (
                                    <Badge
                                        count={pendingTotal}
                                        overflowCount={99}
                                        style={{ backgroundColor: "#ff4d4f" }}
                                    />
                                )}
                            </span>
                        ),
                        value: "pending",
                    },
                ]}
            />

            {view === "active" ? (
                <>
                    <CommunityPostFilterBar
                        filters={filters}
                        onChange={setFilters}
                        categories={categories}
                        hideDays={hideDays}
                    />
                    <Table
                        columns={columns}
                        dataSource={posts}
                        loading={loading}
                        rowKey="postId"
                        pagination={pagination}
                        onChange={handleTableChange}
                        scroll={{ x: "max-content" }}
                        size="middle"
                    />
                </>
            ) : (
                <Table
                    columns={pendingColumns}
                    dataSource={pendingPosts}
                    loading={loading}
                    rowKey="postId"
                    pagination={pendingPagination}
                    onChange={handleTableChange}
                    scroll={{ x: "max-content" }}
                    size="middle"
                />
            )}

            <CommunityPostForm
                open={formOpen}
                editingPost={editingPost}
                categories={categories}
                onClose={() => setFormOpen(false)}
                onSuccess={() => fetchPosts(pagination.current, pagination.pageSize)}
            />

            <CommunityCategoriesModal
                open={categoriesOpen}
                onClose={() => setCategoriesOpen(false)}
                onSaved={setCategories}
            />

            <CommunityPostLimitModal open={postLimitOpen} onClose={() => setPostLimitOpen(false)} />
            <CommunityHideDaysModal
                open={hideDaysOpen}
                onClose={() => setHideDaysOpen(false)}
                onSaved={() => fetchPosts(pagination.current, pagination.pageSize)}
            />
            <CommunityApiKeysModal open={apiKeysOpen} onClose={() => setApiKeysOpen(false)} />
            <CommunityTelegramModal
                open={telegramOpen}
                onClose={() => setTelegramOpen(false)}
                categories={categories}
            />

            <CommunityRejectReasonModal
                open={rejectPostId !== null}
                onClose={() => setRejectPostId(null)}
                onSubmit={handleRejectSubmit}
            />
        </div>
    );
}

export default CommunityPostList;
